import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';

const C = {
  bg:'#141414', bg2:'#1f1f1f', bg3:'#2a2a2a',
  blue:'#0071eb', blueL:'rgba(0,113,235,0.15)', blueB:'rgba(0,113,235,0.4)',
  white:'#ffffff', grey:'#808080', grey2:'#b3b3b3', grey3:'#404040',
  green:'#46d369', red:'#e50914', gold:'#f5a623', border:'rgba(255,255,255,0.1)',
};

const CONTENT_LABELS = {
  sa_original:'🎭 SA Original',sports:'⚽ Sports',
  talk_show:'🎙️ Talk Show',podcast:'🎧 Podcast',
};

function StatCard({icon, label, value, change, color, sub}) {
  return (
    <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:'20px 22px'}}>
      <div style={{fontSize:22,marginBottom:10}}>{icon}</div>
      <div style={{fontSize:10,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',color:C.grey,marginBottom:4}}>{label}</div>
      <div style={{fontSize:28,fontWeight:900,color:color||C.white,lineHeight:1,marginBottom:4}}>{value}</div>
      {change && <div style={{fontSize:11,color:C.green,fontWeight:700}}>{change}</div>}
      {sub && <div style={{fontSize:11,color:C.grey2,marginTop:2}}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard({user, onBack, onHome, onApplications}) {
  const [tab, setTab] = useState('overview');
  const [applications, setApplications] = useState([]);
  const [shows, setShows] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState(null);
  const [appFilter, setAppFilter] = useState('pending');
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [waitlistInterestFilter, setWaitlistInterestFilter] = useState('all');
  const [waitlistDateFilter, setWaitlistDateFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [appsRes, showsRes, waitlistRes] = await Promise.all([
      supabase.from('creator_applications').select('*').order('created_at',{ascending:false}),
      supabase.from('shows').select('*').order('created_at',{ascending:false}),
      supabase.from('viewer_waitlist').select('*').order('created_at',{ascending:false}),
    ]);
    setApplications(appsRes.data || []);
    setShows(showsRes.data || []);
    setWaitlist(waitlistRes.data || []);
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    setUpdating(true);
    await supabase.from('creator_applications').update({status, reviewed_at:new Date().toISOString()}).eq('id',id);
    setToast(status==='approved'?'Creator approved!':'Application declined.');
    setSelected(null);
    loadData();
    setTimeout(()=>setToast(null),3000);
    setUpdating(false);
  };

  const pending = applications.filter(a=>a.status==='pending');
  const approved = applications.filter(a=>a.status==='approved');
  const declined = applications.filter(a=>a.status==='declined');
  const filteredApps = appFilter==='all'?applications:applications.filter(a=>a.status===appFilter);
  const waitlistInterestOptions = ['all', ...Array.from(new Set(waitlist.map(entry => entry.interest).filter(Boolean)))];
  const filteredWaitlist = waitlist.filter(entry => {
    const matchesInterest = waitlistInterestFilter === 'all' || entry.interest === waitlistInterestFilter;
    const q = waitlistSearch.trim().toLowerCase();
    const haystack = [entry.full_name, entry.email, entry.interest, entry.source].filter(Boolean).join(' ').toLowerCase();
    const matchesSearch = q === '' || haystack.includes(q);
    const createdAt = entry.created_at ? new Date(entry.created_at) : null;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(startOfToday);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const matchesDate =
      waitlistDateFilter === 'all' ||
      (waitlistDateFilter === 'today' && createdAt && createdAt >= startOfToday) ||
      (waitlistDateFilter === 'last7' && createdAt && createdAt >= sevenDaysAgo) ||
      (waitlistDateFilter === 'month' && createdAt && createdAt >= startOfMonth);
    return matchesInterest && matchesSearch && matchesDate;
  });

  const exportWaitlist = () => {
    if (filteredWaitlist.length === 0) {
      setToast('No waitlist signups to export for this filter.');
      setTimeout(()=>setToast(null),3000);
      return;
    }

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Full Name', 'Email', 'Interest', 'Source', 'Created At'],
      ...filteredWaitlist.map(entry => [
        entry.full_name || '',
        entry.email || '',
        entry.interest || '',
        entry.source || '',
        entry.created_at ? new Date(entry.created_at).toISOString() : '',
      ]),
    ];

    const csv = rows.map(row => row.map(escapeCsv).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'vaultt-viewer-waitlist.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setToast('Waitlist CSV exported.');
    setTimeout(()=>setToast(null),3000);
  };

  const POOL = {
    subscribers: 1800,
    subPrice: 49,
    gross: 1800*49,
    platformCut: 0.25,
    creatorPool: 1800*49*0.75,
  };

  const tabs = [
    {id:'overview',icon:'📊',label:'Overview'},
    {id:'pool',icon:'💰',label:'Revenue Pool'},
    {id:'creators',icon:'🎬',label:'Creators'},
    {id:'applications',icon:'📋',label:'Applications', badge:pending.length},
    {id:'waitlist',icon:'📬',label:'Waitlist', badge:waitlist.length},
    {id:'content',icon:'🎞️',label:'Content'},
  ];

  return (
    <div style={{background:C.bg,minHeight:'100vh',display:'flex',flexDirection:'column'}}>
      {toast && (
        <div style={{position:'fixed',bottom:24,right:24,background:C.green,color:'#fff',padding:'14px 20px',borderRadius:4,fontSize:13,fontWeight:600,zIndex:9999}}>
          {toast}
        </div>
      )}

      {/* Top bar */}
      <div style={{background:C.bg2,borderBottom:`1px solid ${C.border}`,padding:'0 32px',display:'flex',alignItems:'center',justifyContent:'space-between',height:68,flexShrink:0,boxShadow:'0 18px 50px rgba(0,0,0,0.18)'}}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button onClick={onBack} style={{padding:'8px 14px',background:'rgba(255,255,255,0.05)',border:`1px solid ${C.border}`,borderRadius:999,color:C.white,cursor:'pointer',fontSize:12,fontWeight:700,fontFamily:'inherit'}}>Studio</button>
          <button onClick={onHome} style={{padding:'8px 14px',background:'linear-gradient(135deg,rgba(0,130,255,0.24),rgba(0,88,185,0.16))',border:`1px solid ${C.blueB}`,borderRadius:999,color:C.white,cursor:'pointer',fontSize:12,fontWeight:800,fontFamily:'inherit'}}>Home</button>
          <div style={{width:1,height:20,background:C.border}}/>
          <div style={{fontFamily:'Arial',fontSize:18,fontWeight:900,letterSpacing:4,color:C.blue}}>VAULTT</div>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:'uppercase',color:C.grey,background:C.bg3,padding:'3px 10px',borderRadius:2}}>Admin</div>
        </div>
        <div style={{fontSize:13,color:C.grey2}}>Signed in as {user?.email}</div>
      </div>

      <div style={{display:'flex',flex:1}}>
        {/* Sidebar */}
        <div style={{width:200,background:C.bg2,borderRight:`1px solid ${C.border}`,flexShrink:0,padding:'16px 0'}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'12px 20px',border:'none',background:tab===t.id?C.blueL:'transparent',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:tab===t.id?700:500,color:tab===t.id?C.blue:C.grey2,textAlign:'left',borderLeft:`3px solid ${tab===t.id?C.blue:'transparent'}`,transition:'all 0.18s'}}>
              <span style={{fontSize:16}}>{t.icon}</span>
              <span style={{flex:1}}>{t.label}</span>
              {t.badge>0 && <span style={{background:C.red,color:'#fff',fontSize:10,fontWeight:800,padding:'2px 7px',borderRadius:10,minWidth:18,textAlign:'center'}}>{t.badge}</span>}
            </button>
          ))}
        </div>

        {/* Main content */}
        <div style={{flex:1,overflowY:'auto',padding:'28px 32px'}}>

          {/* OVERVIEW */}
          {tab==='overview' && (
            <div>
              <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>Platform Overview</div>
              <div style={{fontSize:13,color:C.grey,marginBottom:24}}>Real-time metrics for VAULTT</div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:24}}>
                <StatCard icon="👥" label="Total Subscribers" value="1,800" change="+412 this month" color={C.blue}/>
                <StatCard icon="💰" label="Monthly Revenue" value="R88,200" change="+R20,580 vs last month" color={C.green}/>
                <StatCard icon="🎬" label="Active Creators" value={approved.length.toString()} sub={`${pending.length} applications pending`} color={C.gold}/>
                <StatCard icon="🎞️" label="Total Content" value={shows.length.toString()} sub="Videos uploaded" color={C.white}/>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:24}}>
                <StatCard icon="👁" label="Total Views" value="2.4M" change="+18% this month"/>
                <StatCard icon="⏱" label="Total Watch Time" value="184K hrs" change="+22% this month"/>
                <StatCard icon="🎁" label="Fan Tips" value="R18,240" change="+61% this month" color={C.green}/>
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:24,marginBottom:24}}>
                <div style={{fontSize:15,fontWeight:700,color:C.white,marginBottom:16}}>Revenue Summary — Launch Pricing Period</div>
                {[
                  {label:'Gross subscription revenue',val:`R${(POOL.gross).toLocaleString()}`,desc:`${POOL.subscribers} subscribers × R${POOL.subPrice}`},
                  {label:'Platform share (25%)',val:`R${(POOL.gross*0.25).toLocaleString()}`,desc:'VAULTT operating revenue'},
                  {label:'Creator Revenue Pool (75%)',val:`R${Math.round(POOL.creatorPool).toLocaleString()}`,desc:'Distributed by watch time'},
                  {label:'Ad revenue (platform 40%)',val:'R3,640',desc:'From viewer plan ads'},
                  {label:'Total platform revenue',val:'R34,640',color:C.blue,desc:'Before costs'},
                ].map((r,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:i<4?`1px solid ${C.border}`:'none'}}>
                    <div>
                      <div style={{fontSize:13,color:C.grey2}}>{r.label}</div>
                      <div style={{fontSize:11,color:C.grey,marginTop:2}}>{r.desc}</div>
                    </div>
                    <div style={{fontSize:15,fontWeight:800,color:r.color||C.white}}>{r.val}</div>
                  </div>
                ))}
              </div>

              {pending.length > 0 && (
                <div style={{background:'rgba(229,9,20,0.1)',border:'1px solid rgba(229,9,20,0.3)',borderRadius:4,padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center',cursor:'pointer'}} onClick={()=>setTab('applications')}>
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:C.red}}/>
                    <div style={{fontSize:14,fontWeight:700,color:C.white}}>{pending.length} creator application{pending.length>1?'s':''} waiting for review</div>
                  </div>
                  <div style={{fontSize:13,color:C.blue,fontWeight:600}}>Review now →</div>
                </div>
              )}
            </div>
          )}

          {/* POOL */}
          {tab==='pool' && (
            <div>
              <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>Revenue Pool</div>
              <div style={{fontSize:13,color:C.grey,marginBottom:24}}>How subscription revenue is distributed to creators</div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
                <div style={{background:C.bg2,border:`1px solid ${C.blueB}`,borderRadius:4,padding:24}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',color:C.blue,marginBottom:8}}>Total Pool This Month</div>
                  <div style={{fontFamily:'Arial',fontSize:48,fontWeight:900,color:C.white,lineHeight:1,marginBottom:4}}>R{Math.round(POOL.creatorPool).toLocaleString()}</div>
                  <div style={{fontSize:13,color:C.grey2}}>75% of R{POOL.gross.toLocaleString()} gross revenue</div>
                </div>
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:24}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:1.5,textTransform:'uppercase',color:C.grey,marginBottom:8}}>Platform Revenue</div>
                  <div style={{fontFamily:'Arial',fontSize:48,fontWeight:900,color:C.white,lineHeight:1,marginBottom:4}}>R{Math.round(POOL.gross*0.25).toLocaleString()}</div>
                  <div style={{fontSize:13,color:C.grey2}}>25% platform share after costs</div>
                </div>
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:24,marginBottom:16}}>
                <div style={{fontSize:15,fontWeight:700,color:C.white,marginBottom:16}}>Pool Split Rules</div>
                {[
                  {label:'Founding Creator share',val:'75%',desc:'For first 12 months after joining',color:C.green},
                  {label:'Standard Creator share',val:'70%',desc:'After founding period expires',color:C.blue},
                  {label:'Direct subscription',val:'80%',desc:'Creator keeps from direct channel subscribers',color:C.gold},
                  {label:'Fan tips',val:'95%',desc:'Creator keeps from viewer tips',color:C.green},
                  {label:'Ad revenue',val:'60%',desc:'Creator share of ads shown on their content',color:C.blue},
                ].map((r,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:i<4?`1px solid ${C.border}`:'none'}}>
                    <div>
                      <div style={{fontSize:13,color:C.white,fontWeight:600}}>{r.label}</div>
                      <div style={{fontSize:11,color:C.grey,marginTop:2}}>{r.desc}</div>
                    </div>
                    <div style={{fontSize:20,fontWeight:900,color:r.color}}>{r.val}</div>
                  </div>
                ))}
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:24}}>
                <div style={{fontSize:15,fontWeight:700,color:C.white,marginBottom:16}}>How Pool Distribution Works</div>
                <div style={{fontSize:13,color:C.grey2,lineHeight:1.8}}>
                  The Creator Revenue Pool is distributed monthly based on <strong style={{color:C.white}}>watch time share</strong>. A creator who accounts for 8% of total platform watch time receives 8% of the pool. This rewards creators who produce engaging content that keeps viewers watching longer.
                  <br/><br/>
                  Example: Pool = R{Math.round(POOL.creatorPool).toLocaleString()} · Creator with 8% watch time share = R{Math.round(POOL.creatorPool*0.08).toLocaleString()} from pool + their ad revenue + tips + direct subscribers.
                </div>
              </div>
            </div>
          )}

          {/* CREATORS */}
          {tab==='creators' && (
            <div>
              <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>Creators</div>
              <div style={{fontSize:13,color:C.grey,marginBottom:24}}>{approved.length} approved creators on the platform</div>

              {approved.length === 0 ? (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:48,textAlign:'center'}}>
                  <div style={{fontSize:40,marginBottom:12}}>🎬</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.white,marginBottom:6}}>No approved creators yet</div>
                  <div style={{fontSize:13,color:C.grey,marginBottom:20}}>Approve creator applications to see them here.</div>
                  <button onClick={()=>setTab('applications')} style={{padding:'10px 24px',background:C.blue,color:'#fff',border:'none',borderRadius:4,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Review Applications</button>
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
                  {approved.map(c=>(
                    <div key={c.id} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:20}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
                        <div>
                          <div style={{fontSize:15,fontWeight:700,color:C.white,marginBottom:2}}>{c.full_name}</div>
                          <div style={{fontSize:11,color:C.grey}}>{c.email}</div>
                        </div>
                        <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:2,background:'rgba(70,211,105,0.15)',color:C.green,border:'1px solid rgba(70,211,105,0.3)'}}>ACTIVE</span>
                      </div>
                      <div style={{fontSize:12,color:C.blue,fontWeight:600,marginBottom:12}}>{CONTENT_LABELS[c.content_type]||c.content_type}</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        {[{l:'Pool Share',v:'—'},{l:'Monthly Earn',v:'—'},{l:'Subscribers',v:'0'},{l:'Videos',v:shows.filter(s=>s.creator_id===c.id).length.toString()}].map((s,i)=>(
                          <div key={i} style={{background:C.bg3,borderRadius:2,padding:'8px 10px'}}>
                            <div style={{fontSize:9,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:2}}>{s.l}</div>
                            <div style={{fontSize:14,fontWeight:700,color:C.white}}>{s.v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* APPLICATIONS */}
          {tab==='applications' && (
            <div style={{display:'grid',gridTemplateColumns:'1fr 380px',gap:20,height:'calc(100vh - 160px)'}}>
              <div style={{display:'flex',flexDirection:'column'}}>
                <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>Creator Applications</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:16}}>
                  {[{l:'Pending',v:pending.length,c:C.gold},{l:'Approved',v:approved.length,c:C.green},{l:'Declined',v:declined.length,c:C.red}].map((s,i)=>(
                    <div key={i} style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:'12px 16px'}}>
                      <div style={{fontSize:10,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>{s.l}</div>
                      <div style={{fontSize:24,fontWeight:900,color:s.c}}>{s.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{display:'flex',gap:0,marginBottom:12,borderBottom:`1px solid ${C.border}`}}>
                  {['pending','approved','declined','all'].map(f=>(
                    <button key={f} onClick={()=>setAppFilter(f)}
                      style={{padding:'10px 16px',border:'none',background:'transparent',cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,color:appFilter===f?C.blue:C.grey,borderBottom:`2px solid ${appFilter===f?C.blue:'transparent'}`,textTransform:'capitalize',marginBottom:-1}}>
                      {f==='all'?'All':f}
                    </button>
                  ))}
                </div>
                <div style={{overflowY:'auto',flex:1}}>
                  {filteredApps.length===0 ? (
                    <div style={{padding:32,textAlign:'center',color:C.grey}}>No {appFilter} applications</div>
                  ) : filteredApps.map(app=>(
                    <div key={app.id} onClick={()=>setSelected(app)}
                      style={{padding:'14px 16px',borderBottom:`1px solid ${C.border}`,cursor:'pointer',background:selected?.id===app.id?C.blueL:'transparent',transition:'background 0.18s',borderLeft:`3px solid ${selected?.id===app.id?C.blue:'transparent'}`}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                        <div style={{fontSize:14,fontWeight:700,color:C.white}}>{app.full_name}</div>
                        <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:2,background:app.status==='approved'?'rgba(70,211,105,0.15)':app.status==='declined'?'rgba(229,9,20,0.15)':'rgba(245,166,35,0.15)',color:app.status==='approved'?C.green:app.status==='declined'?C.red:C.gold,textTransform:'uppercase',letterSpacing:0.5}}>
                          {app.status}
                        </span>
                      </div>
                      <div style={{fontSize:11,color:C.grey,marginBottom:3}}>{app.email}</div>
                      <div style={{fontSize:11,color:C.blue,fontWeight:600}}>{CONTENT_LABELS[app.content_type]||app.content_type}</div>
                      <div style={{fontSize:10,color:C.grey3,marginTop:3}}>{new Date(app.created_at).toLocaleDateString('en-ZA',{day:'numeric',month:'short',year:'numeric'})}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,overflowY:'auto'}}>
                {!selected ? (
                  <div style={{padding:40,textAlign:'center',paddingTop:60}}>
                    <div style={{fontSize:36,marginBottom:12}}>👈</div>
                    <div style={{fontSize:13,color:C.grey}}>Select an application to review</div>
                  </div>
                ) : (
                  <div style={{padding:20}}>
                    <div style={{fontSize:18,fontWeight:800,color:C.white,marginBottom:4}}>{selected.full_name}</div>
                    <div style={{fontSize:12,color:C.blue,fontWeight:600,marginBottom:16}}>{CONTENT_LABELS[selected.content_type]||selected.content_type}</div>
                    {[{l:'Email',v:selected.email},{l:'Phone',v:selected.phone||'Not provided'},{l:'Applied',v:new Date(selected.created_at).toLocaleDateString('en-ZA',{day:'numeric',month:'long',year:'numeric'})}].map((f,i)=>(
                      <div key={i} style={{padding:'10px 12px',background:C.bg3,borderRadius:2,marginBottom:8}}>
                        <div style={{fontSize:9,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:3}}>{f.l}</div>
                        <div style={{fontSize:13,color:C.white}}>{f.v}</div>
                      </div>
                    ))}
                    {(selected.social_instagram||selected.social_tiktok||selected.social_youtube)&&(
                      <div style={{padding:'10px 12px',background:C.bg3,borderRadius:2,marginBottom:8}}>
                        <div style={{fontSize:9,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Social Media</div>
                        {selected.social_instagram&&<div style={{fontSize:12,color:C.white,marginBottom:3}}>📸 instagram.com/{selected.social_instagram}</div>}
                        {selected.social_tiktok&&<div style={{fontSize:12,color:C.white,marginBottom:3}}>🎵 tiktok.com/@{selected.social_tiktok}</div>}
                        {selected.social_youtube&&<div style={{fontSize:12,color:C.white}}>▶️ youtube.com/@{selected.social_youtube}</div>}
                      </div>
                    )}
                    {selected.sample_work_url&&(
                      <div style={{padding:'10px 12px',background:C.bg3,borderRadius:2,marginBottom:8}}>
                        <div style={{fontSize:9,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>Sample Work</div>
                        <a href={selected.sample_work_url} target="_blank" rel="noreferrer" style={{fontSize:12,color:C.blue,wordBreak:'break-all'}}>{selected.sample_work_url}</a>
                      </div>
                    )}
                    <div style={{padding:'12px',background:C.bg3,borderRadius:2,marginBottom:16}}>
                      <div style={{fontSize:9,color:C.grey,letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Why VAULTT</div>
                      <div style={{fontSize:12,color:C.white,lineHeight:1.65}}>{selected.why_vaultt}</div>
                    </div>
                    {selected.status==='pending'&&(
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                        <button onClick={()=>updateStatus(selected.id,'declined')} disabled={updating} style={{padding:'11px 0',border:'1px solid rgba(229,9,20,0.5)',borderRadius:4,background:'rgba(229,9,20,0.1)',color:C.red,cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700}}>✕ Decline</button>
                        <button onClick={()=>updateStatus(selected.id,'approved')} disabled={updating} style={{padding:'11px 0',border:'none',borderRadius:4,background:C.green,color:'#000',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700}}>✓ Approve</button>
                      </div>
                    )}
                    {selected.status==='approved'&&<div style={{padding:12,background:'rgba(70,211,105,0.1)',borderRadius:4,fontSize:13,color:C.green,fontWeight:600,textAlign:'center'}}>✓ Approved</div>}
                    {selected.status==='declined'&&(
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        <div style={{padding:12,background:'rgba(229,9,20,0.1)',borderRadius:4,fontSize:13,color:C.red,fontWeight:600,textAlign:'center'}}>✕ Declined</div>
                        <button onClick={()=>updateStatus(selected.id,'approved')} style={{padding:'11px 0',border:'none',borderRadius:4,background:C.green,color:'#000',cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700}}>Reconsider — Approve</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WAITLIST */}
          {tab==='waitlist' && (
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'end',gap:16,flexWrap:'wrap',marginBottom:24}}>
                <div>
                  <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>Viewer Waitlist</div>
                  <div style={{fontSize:13,color:C.grey}}>{filteredWaitlist.length} of {waitlist.length} viewer signups shown</div>
                </div>
                <button
                  onClick={exportWaitlist}
                  style={{padding:'10px 18px',background:C.blue,color:'#fff',border:'none',borderRadius:4,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}
                >
                  Export CSV
                </button>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
                <StatCard icon="📬" label="Total Signups" value={waitlist.length.toString()} sub="All captured viewer leads" color={C.blue}/>
                <StatCard icon="🎭" label="SA Originals" value={waitlist.filter(w=>w.interest==='SA Originals').length.toString()} sub="Interest in originals" color={C.white}/>
                <StatCard icon="⚽" label="Sports" value={waitlist.filter(w=>w.interest==='Sports').length.toString()} sub="Interest in sports" color={C.white}/>
                <StatCard icon="🎧" label="Podcasts" value={waitlist.filter(w=>w.interest==='Podcasts').length.toString()} sub="Interest in podcasts" color={C.white}/>
              </div>

              <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap',marginBottom:20}}>
                <input
                  value={waitlistSearch}
                  onChange={e=>setWaitlistSearch(e.target.value)}
                  placeholder="Search by name, email, interest..."
                  style={{flex:'1 1 320px',padding:'12px 14px',background:C.bg2,color:C.white,border:`1px solid ${C.border}`,borderRadius:4,fontSize:13,fontFamily:'inherit'}}
                />
                <select
                  value={waitlistInterestFilter}
                  onChange={e=>setWaitlistInterestFilter(e.target.value)}
                  style={{padding:'12px 14px',background:C.bg2,color:C.white,border:`1px solid ${C.border}`,borderRadius:4,fontSize:13,fontFamily:'inherit',minWidth:180}}
                >
                  {waitlistInterestOptions.map(option => (
                    <option key={option} value={option} style={{background:'#141414', color:'#ffffff'}}>
                      {option === 'all' ? 'All interests' : option}
                    </option>
                  ))}
                </select>
                <select
                  value={waitlistDateFilter}
                  onChange={e=>setWaitlistDateFilter(e.target.value)}
                  style={{padding:'12px 14px',background:C.bg2,color:C.white,border:`1px solid ${C.border}`,borderRadius:4,fontSize:13,fontFamily:'inherit',minWidth:180}}
                >
                  <option value="all" style={{background:'#141414', color:'#ffffff'}}>All time</option>
                  <option value="today" style={{background:'#141414', color:'#ffffff'}}>Today</option>
                  <option value="last7" style={{background:'#141414', color:'#ffffff'}}>Last 7 days</option>
                  <option value="month" style={{background:'#141414', color:'#ffffff'}}>This month</option>
                </select>
              </div>

              {waitlist.length===0 ? (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:48,textAlign:'center'}}>
                  <div style={{fontSize:40,marginBottom:12}}>📭</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.white,marginBottom:6}}>No waitlist signups yet</div>
                  <div style={{fontSize:13,color:C.grey}}>Viewer leads from the homepage will appear here.</div>
                </div>
              ) : filteredWaitlist.length===0 ? (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:48,textAlign:'center'}}>
                  <div style={{fontSize:40,marginBottom:12}}>🔎</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.white,marginBottom:6}}>No signups match this filter</div>
                  <div style={{fontSize:13,color:C.grey}}>Try a different search or interest filter.</div>
                </div>
              ) : (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,overflow:'hidden'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1.4fr 1.6fr 1fr 1fr 1fr',padding:'10px 16px',borderBottom:`1px solid ${C.border}`,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:C.grey}}>
                    <span>Name</span><span>Email</span><span>Interest</span><span>Source</span><span>Joined</span>
                  </div>
                  {filteredWaitlist.map((entry,i)=>(
                    <div key={entry.id || `${entry.email}-${i}`} style={{display:'grid',gridTemplateColumns:'1.4fr 1.6fr 1fr 1fr 1fr',padding:'12px 16px',borderBottom:i<filteredWaitlist.length-1?`1px solid ${C.border}`:'none',alignItems:'center',gap:12}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.white}}>{entry.full_name || 'Not provided'}</div>
                      <div style={{fontSize:12,color:C.grey2,wordBreak:'break-word'}}>{entry.email}</div>
                      <div style={{fontSize:12,color:C.blue,fontWeight:600}}>{entry.interest || 'Not specified'}</div>
                      <div style={{fontSize:11,color:C.grey2,textTransform:'capitalize'}}>{(entry.source || 'landing_page').replace(/_/g,' ')}</div>
                      <div style={{fontSize:11,color:C.grey}}>{entry.created_at ? new Date(entry.created_at).toLocaleDateString('en-ZA',{day:'numeric',month:'short',year:'numeric'}) : '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CONTENT */}
          {tab==='content' && (
            <div>
              <div style={{fontSize:22,fontWeight:800,color:C.white,marginBottom:4}}>All Content</div>
              <div style={{fontSize:13,color:C.grey,marginBottom:24}}>{shows.length} videos on the platform</div>
              {shows.length===0 ? (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,padding:48,textAlign:'center'}}>
                  <div style={{fontSize:40,marginBottom:12}}>🎞️</div>
                  <div style={{fontSize:16,fontWeight:700,color:C.white,marginBottom:6}}>No content uploaded yet</div>
                  <div style={{fontSize:13,color:C.grey}}>Content appears here once creators upload videos.</div>
                </div>
              ) : (
                <div style={{background:C.bg2,border:`1px solid ${C.border}`,borderRadius:4,overflow:'hidden'}}>
                  <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 80px',padding:'10px 16px',borderBottom:`1px solid ${C.border}`,fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',color:C.grey}}>
                    <span>Title</span><span>Category</span><span>Type</span><span>Uploaded</span><span>Mux</span>
                  </div>
                  {shows.map((s,i)=>(
                    <div key={s.id} style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 80px',padding:'12px 16px',borderBottom:i<shows.length-1?`1px solid ${C.border}`:'none',alignItems:'center'}}>
                      <div style={{fontSize:13,fontWeight:600,color:C.white,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{s.title}</div>
                      <div style={{fontSize:12,color:C.grey2}}>{s.category}</div>
                      <div style={{fontSize:12,color:C.grey2}}>{s.type}</div>
                      <div style={{fontSize:11,color:C.grey}}>{new Date(s.created_at).toLocaleDateString('en-ZA',{day:'numeric',month:'short'})}</div>
                      <div style={{fontSize:11,fontWeight:600}}>{s.mux_playback_id?<span style={{color:C.green}}>✓ Live</span>:<span style={{color:C.grey}}>Pending</span>}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
